import { Button } from "components/DesignSystem/Button/Button";
import { INCORPORATION_FORM_SOURCE } from "constants/incorporation";
import { usePageTitle } from "hooks/usePageTitle";
import { useToast } from "hooks/useToast";
import { useModal } from "hooks/useModal";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import {
  Stage,
  usePublishFormInstanceMutation,
  useDeleteFormInstanceMutation,
} from "store/apis/incorporationFormBuilder";
import { useHistory, useParams } from "react-router-dom";
import { BackendError } from "types/utils/error";
import { isFormTreeComplete } from "util/incorporationFormProgress";
import { IncorporationFormBuilder } from "./IncorporationFormBuilder";
import { SaveAsBaseTemplateModal } from "./SaveAsBaseTemplateModal";
import { DiscardTemplateModal } from "./DiscardTemplateModal";
import { UnpublishTemplateModal } from "./UnpublishTemplateModal";
import { IncorporationSaveStatus } from "./IncorporationSaveStatus";
import { IncorporationTrackerActionsMenu } from "./IncorporationTrackerActionsMenu";
import { RearrangeStepsModal } from "./RearrangeStepsModal";
import { ReviewEditorToggle } from "./ReviewEditorToggle";
import { useIncorporationFormTree } from "./useIncorporationFormTree";

const INCORPORATION_TRACKER_PATH = "/admin/incorporation/tracker";
const EMPTY_STAGES: Stage[] = [];

type RouteParams = {
  formInstanceId: string;
};

export const IncorporationTrackerFormBuilder = () => {
  usePageTitle("Incorporation Form");
  const { alertToast, successToast } = useToast();
  const history = useHistory();
  const { formInstanceId } = useParams<RouteParams>();
  const query = useQuery();
  const { update } = useUpdateQuery();
  const isEditOverride = query.get("edit") === "true";
  const saveAsTemplateModal = useModal();
  const discardTemplateModal = useModal();
  const unpublishTemplateModal = useModal();
  const rearrangeStepsModal = useModal();

  const { data, isLoading, isSuccess } = useIncorporationFormTree({
    source: INCORPORATION_FORM_SOURCE.INSTANCE,
    contentId: formInstanceId,
  });

  const [publishFormInstance, { isLoading: isPublishing }] =
    usePublishFormInstanceMutation();
  const [deleteFormInstance, { isLoading: isDiscarding }] =
    useDeleteFormInstanceMutation();

  const groupName = data?.company_group_name;
  const isEditable = isEditOverride;
  const isFormComplete = isFormTreeComplete(data);

  let pageTitle = "Untitled template";
  if (groupName) {
    pageTitle = `${groupName} template`;
  }

  const isPublished = Boolean(data?.is_published);

  const onPublish = async () => {
    try {
      await publishFormInstance({
        formInstanceId,
        isPublished: true,
      }).unwrap();
      successToast({ message: `Template published for customer` });
      history.push(INCORPORATION_TRACKER_PATH);
    } catch (err) {
      alertToast({
        message:
          (err as BackendError)?.data?.error?.message ?? "Something went wrong",
      });
    }
  };

  const onConfirmUnpublish = async () => {
    try {
      await publishFormInstance({
        formInstanceId,
        isPublished: false,
      }).unwrap();
      unpublishTemplateModal.close();
      history.push(INCORPORATION_TRACKER_PATH);
    } catch (err) {
      alertToast({
        message:
          (err as BackendError)?.data?.error?.message ?? "Something went wrong",
      });
    }
  };

  const onEditTemplateClick = () => {
    update({ query: "edit", value: "true" });
  };

  const onPreviewTemplateClick = () => {
    update({ query: "edit", value: null });
  };

  const onConfirmDiscardTemplate = async () => {
    try {
      await deleteFormInstance({ formInstanceId }).unwrap();
      discardTemplateModal.close();
      history.push(INCORPORATION_TRACKER_PATH);
    } catch (err) {
      alertToast({
        message:
          (err as BackendError)?.data?.error?.message ?? "Something went wrong",
      });
    }
  };

  const headerRight = (
    <div className="t-flex t-items-center t-gap-3">
      <ReviewEditorToggle
        isEditable={isEditable}
        isPublished={isPublished}
        onPreview={onPreviewTemplateClick}
        onEdit={onEditTemplateClick}
      />
      <IncorporationTrackerActionsMenu
        isEditable={isEditable}
        isPublished={isPublished}
        onSaveAsBaseTemplate={saveAsTemplateModal.open}
        onRearrangeSteps={rearrangeStepsModal.open}
        onDiscardTemplate={discardTemplateModal.open}
        onUnpublish={unpublishTemplateModal.open}
      />
    </div>
  );

  let footer;
  if (!isPublished) {
    footer = (
      <div className="t-flex t-items-center t-justify-between t-gap-2 t-border-0 t-border-t t-border-solid t-border-neutral-10 t-py-4 t-pl-4 t-pr-4">
        <div>{isEditable && <IncorporationSaveStatus />}</div>
        <Button
          type="button"
          customType="primary"
          isLoading={isPublishing}
          disabled={isPublishing || !isFormComplete}
          onClick={onPublish}
        >
          Publish for {groupName || "company"}
        </Button>
      </div>
    );
  } else if (isEditable) {
    footer = (
      <div className="t-flex t-items-center t-border-0 t-border-t t-border-solid t-border-neutral-10 t-py-4 t-pl-4 t-pr-4">
        <IncorporationSaveStatus />
      </div>
    );
  }

  return (
    <>
      <IncorporationFormBuilder
        source={INCORPORATION_FORM_SOURCE.INSTANCE}
        contentId={formInstanceId}
        data={data}
        isLoading={isLoading}
        isSuccess={isSuccess}
        isEditable={isEditable}
        pageTitle={pageTitle}
        breadcrumbName="Tracker"
        breadcrumbLink={INCORPORATION_TRACKER_PATH}
        headerRight={isSuccess && headerRight}
        footer={footer}
        isAnswerable={isPublished}
      />
      <SaveAsBaseTemplateModal
        isOpen={saveAsTemplateModal.isOpen}
        close={saveAsTemplateModal.close}
        formInstanceId={formInstanceId}
      />
      <DiscardTemplateModal
        isOpen={discardTemplateModal.isOpen}
        close={discardTemplateModal.close}
        onDiscard={onConfirmDiscardTemplate}
        isLoading={isDiscarding}
      />
      <UnpublishTemplateModal
        isOpen={unpublishTemplateModal.isOpen}
        close={unpublishTemplateModal.close}
        onUnpublish={onConfirmUnpublish}
        isLoading={isPublishing}
      />
      <RearrangeStepsModal
        isOpen={rearrangeStepsModal.isOpen}
        close={rearrangeStepsModal.close}
        treeId={formInstanceId}
        source={INCORPORATION_FORM_SOURCE.INSTANCE}
        stages={data?.stages ?? EMPTY_STAGES}
      />
    </>
  );
};
